import React from 'react';
import { Box, Button, ButtonBase, Drawer, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { rightDrawerBelowAppBarSx } from '../../theme/layoutConstants';
import { Bot, Company, Snapshot } from './lib/types';
import { tint } from './lib/tone';
import { STATUS_LABEL, botBySlug } from './lib/selectors';
import { fmtIct } from './lib/ict';
import { DRAWER_TABS, DrawerTab } from './hooks/useBotOpsUrlState';
import { NoReviewChip, PriorityChip, ProvLine, VerdictChip } from './chips';
import { StatusDot } from './StatusDot';
import { ACCENT, FONT, OVERLINE, RADIUS, TONE, accentText } from './lib/brand';

const TAB_LABEL: Record<DrawerTab, string> = { inbox: 'Inbox', actions: 'Action items', verdict: 'C-suite verdict', findings: 'Findings', detail: 'Detail' };

const Section = React.forwardRef<HTMLDivElement, { id: string; title: string; children: React.ReactNode }>(({ id, title, children }, ref) => (
  <Box ref={ref} id={id} component="section" sx={{ mb: 2.5, scrollMarginTop: 56 }}>
    <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', fontSize: 10.5, mb: 0.6 }}>{title}</Typography>
    {children}
  </Box>
));
Section.displayName = 'Section';

/**
 * The quick-preview drawer for one bot (560 px, right). Tabs scroll to a section
 * and write `tab=` to the URL. The Inbox section is a slot: Phase 1 shows the
 * read-only note, Phase 2 supplies the composer and threads.
 */
export function BotDrawer({
  snapshot, company, bot, tab, onTab, onClose, onShowInActionCenter, onOpenAction, onOpenBot, onOpenFullPage, renderInbox, inboxUnread = 0,
}: {
  snapshot: Snapshot; company: Company; bot: Bot | null; tab: DrawerTab;
  onTab: (t: DrawerTab) => void; onClose: () => void; onShowInActionCenter: (b: Bot) => void;
  onOpenAction: (id: string) => void; onOpenBot: (slug: string) => void; onOpenFullPage?: (b: Bot) => void;
  renderInbox?: (bot: Bot) => React.ReactNode; inboxUnread?: number;
}) {
  const refs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const open = Boolean(bot);

  // Opening on a tab scrolls to that section once the content is mounted.
  React.useEffect(() => {
    if (!bot) return;
    const el = refs.current[tab];
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bot && bot.slug, tab]);

  const accent = ACCENT[company].acc;
  const actions = (bot && bot.actions) || [];
  const reporting = bot ? [
    ['Coordinator', bot.coordinator], ['Reports to', bot.reports_to_label || bot.reports_to],
    ['Exec leads', (bot.exec_leads || []).join(', ')],
  ].filter((r) => r[1]) as [string, string][] : [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-label': bot ? `${bot.name} details` : 'Bot details',
        sx: { ...rightDrawerBelowAppBarSx, width: { xs: '100%', sm: 560 } },
        'data-testid': 'bot-drawer',
      }}
    >
      {bot && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Box sx={{ p: 2, bgcolor: tint(ACCENT[company].soft), borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StatusDot color={bot.color} size={12} />
              <Typography component="h2" sx={{ fontFamily: FONT.heading, fontSize: 18, fontWeight: 700, flex: 1, minWidth: 0 }}>{bot.name}</Typography>
              <IconButton aria-label="Close" onClick={onClose} size="small" autoFocus>✕</IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {STATUS_LABEL[bot.color]} · status file says “{bot.status_raw || 'no status file'}” · last activity {fmtIct(bot.last_activity_ict)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ fontFamily: FONT.mono }}>agent {bot.agent_id} · slug {bot.slug}</Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {onOpenFullPage && <Button size="small" variant="contained" onClick={() => onOpenFullPage(bot)}>Open full page →</Button>}
              <Button size="small" variant="outlined" onClick={() => onShowInActionCenter(bot)}>Show in Action Center</Button>
            </Box>
          </Box>

          <Tabs
            value={tab}
            onChange={(_e, v: DrawerTab) => onTab(v)}
            variant="scrollable"
            scrollButtons={false}
            aria-label="Bot sections"
            sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontSize: 13 } }}
          >
            {DRAWER_TABS.map((t) => (
              <Tab
                key={t}
                value={t}
                label={t === 'actions' ? `${TAB_LABEL[t]} (${actions.length})` : t === 'inbox' && inboxUnread ? `${TAB_LABEL[t]} (${inboxUnread})` : TAB_LABEL[t]}
              />
            ))}
          </Tabs>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <Section id="bo-d-inbox" title="Inbox" ref={(el) => { refs.current.inbox = el; }}>
              {renderInbox ? renderInbox(bot) : (
                <Typography variant="body2" color="text.secondary" data-testid="inbox-placeholder">No instructions yet. Instructions and questions for this bot are switched on in the next release.</Typography>
              )}
            </Section>

            {(bot.blockers || []).length > 0 && (
              <Section id="bo-d-blockers" title="Blockers">
                {(bot.blockers || []).map((b) => (
                  <Box key={b.text} sx={{ border: `1px solid ${TONE.bad.border}`, bgcolor: tint(TONE.bad.bg), borderRadius: RADIUS.lg, p: 1.2, mb: 0.8 }}>
                    <Typography sx={{ fontSize: 13 }}>{b.text}</Typography>
                    <ProvLine prov={b.prov} fmt={fmtIct} />
                  </Box>
                ))}
              </Section>
            )}

            <Section id="bo-d-doing" title="Currently doing">
              <Typography sx={{ fontSize: 13.5 }}>{(bot.doing && bot.doing.text) || 'No status file found'}</Typography>
              {bot.doing && <ProvLine prov={bot.doing.prov} fmt={fmtIct} />}
            </Section>

            <Section id="bo-d-rec" title="Bot recommendation">
              {bot.recommendation && bot.recommendation.text ? (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.lg, p: 1.2 }}>
                  <Typography sx={{ fontSize: 13.5 }}>{bot.recommendation.text}</Typography>
                  {bot.recommendation.for && <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 700, mt: 0.5 }}>FOR {bot.recommendation.for}</Typography>}
                  <ProvLine prov={bot.recommendation.prov} fmt={fmtIct} />
                </Box>
              ) : <Typography variant="body2" color="text.secondary">Not stated in source</Typography>}
            </Section>

            <Section id="bo-d-verdict" title="C-suite verdict" ref={(el) => { refs.current.verdict = el; }}>
              {(bot.reviews || []).length === 0 ? <NoReviewChip /> : (bot.reviews || []).map((r, i) => (
                <Box key={r.id || i} sx={{ border: '1px solid', borderColor: r.verdict === 'alternative' ? TONE.warn.border : TONE.good.border, bgcolor: tint(r.verdict === 'alternative' ? TONE.warn.bg : TONE.good.bg), borderRadius: RADIUS.lg, p: 1.2, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <VerdictChip exec={r.exec} verdict={r.verdict} />
                    {r.awaiting_grant && <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, px: 0.8, borderRadius: RADIUS.sm, bgcolor: TONE.warn.bg, color: TONE.warn.fg }}>AWAITING GRANT</Box>}
                  </Box>
                  {r.item && <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>On “{r.item}”</Typography>}
                  <Typography sx={{ fontSize: 13 }}>{r.text || 'Not stated in source'}</Typography>
                  <ProvLine prov={r.prov} fmt={fmtIct} />
                </Box>
              ))}
            </Section>

            <Section id="bo-d-findings" title={`Top findings (${Math.min(4, (bot.findings || []).length)} of ${bot.findings_total ?? (bot.findings || []).length})`} ref={(el) => { refs.current.findings = el; }}>
              {(bot.findings || []).length === 0 ? <Typography variant="body2" color="text.secondary">No scored findings in this bot&apos;s files.</Typography> : (bot.findings || []).slice(0, 4).map((f) => {
                const linked = actions.find((a) => a.for === f.title);
                const body = (
                  <Box sx={{ textAlign: 'left', width: '100%' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                      {typeof f.score === 'number' && <Box component="span" sx={{ fontFamily: FONT.mono, fontSize: 11, px: 0.7, mr: 0.7, borderRadius: RADIUS.sm, bgcolor: 'action.selected' }}>{f.score}</Box>}
                      {f.title}
                    </Typography>
                    {f.bucket && <Typography variant="caption" color="text.secondary" component="div">{f.bucket}</Typography>}
                    {f.problem && <Typography sx={{ fontSize: 12.5, mt: 0.5 }}><b>Problem</b> {f.problem}</Typography>}
                    {f.solution && <Typography sx={{ fontSize: 12.5, mt: 0.4 }}><b>Proposed solution</b> {f.solution}</Typography>}
                    <ProvLine prov={f.prov} fmt={fmtIct} />
                  </Box>
                );
                return linked ? (
                  <ButtonBase key={f.title} onClick={() => onOpenAction(linked.id)} sx={{ display: 'block', width: '100%', border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.lg, p: 1.2, mb: 1 }}>{body}</ButtonBase>
                ) : (
                  <Box key={f.title} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.lg, p: 1.2, mb: 1 }}>{body}</Box>
                );
              })}
            </Section>

            <Section id="bo-d-actions" title={`Action items (${actions.length})`}>
              {actions.length === 0 ? <Typography variant="body2" color="text.secondary">No action items found in this bot&apos;s files.</Typography> : actions.map((a) => (
                <ButtonBase key={a.id} onClick={() => onOpenAction(a.id)} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', textAlign: 'left', width: '100%', py: 0.8, borderTop: '1px solid', borderColor: 'divider' }}>
                  <PriorityChip priority={a.priority} />
                  <Typography sx={{ fontSize: 12.5, flex: 1 }}>{a.for || a.text}</Typography>
                </ButtonBase>
              ))}
            </Section>

            {bot.latest_output && bot.latest_output.title && (
              <Section id="bo-d-latest" title="Latest output">
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{bot.latest_output.title}</Typography>
                {(bot.latest_output.headings || []).map((h) => <Typography key={h} variant="caption" color="text.secondary" component="div">• {h}</Typography>)}
                <ProvLine prov={bot.latest_output.prov} fmt={fmtIct} />
              </Section>
            )}

            <Section id="bo-d-detail" title="Reporting" ref={(el) => { refs.current.detail = el; }}>
              {reporting.length === 0 ? <Typography variant="body2" color="text.secondary">Not stated in source</Typography> : reporting.map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', gap: 1.5, fontSize: 13, py: 0.3 }}>
                  <Box sx={{ width: 100, color: 'text.secondary' }}>{k}</Box>
                  <Box>
                    {k === 'Coordinator' || k === 'Reports to'
                      ? (botBySlug(snapshot, v) ? <ButtonBase onClick={() => onOpenBot(v)} sx={{ color: accentText(accent), textDecoration: 'underline', fontSize: 13 }}>{botBySlug(snapshot, v)?.name}</ButtonBase> : v)
                      : v}
                  </Box>
                </Box>
              ))}
              <Box sx={{ mt: 1 }}><ProvLine prov={bot.roster_prov} fmt={fmtIct} /></Box>
            </Section>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
